import { useQuery } from '@tanstack/react-query';
import { Radio, Users, LogIn } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatDateTime } from '../../lib/dateFmt';
import { api } from '../../lib/api';

interface ActivityUser {
  user_id: string;
  email: string;
  display_name: string;
  roles: string[];
  login_method: string | null;
  login_at: string | null;
  last_activity_at: string;
  connected: boolean;
  socket_count: number;
  /** Which portal they are on; null for sessions that predate the stamp. */
  portal: 'main' | 'training' | null;
}

interface ActivityResponse {
  window_minutes: number;
  generated_at: string;
  users: ActivityUser[];
}

interface LoginRecord {
  id: string;
  user_id: string;
  person_id: string | null;
  email: string;
  display_name: string | null;
  method: string;
  created_at: string;
}

interface LoginsResponse {
  hours: number;
  since: string;
  logins: LoginRecord[];
}

/** One row per user: how many times they logged in and when, over the window. */
interface LoginRollup {
  user_id: string;
  display_name: string;
  email: string;
  count: number;
  first_at: string;
  last_at: string;
  methods: string[];
}

function rollupLogins(logins: LoginRecord[]): LoginRollup[] {
  const byUser = new Map<string, LoginRollup>();
  // The API returns newest first; walk it so first/last land correctly.
  for (const l of logins) {
    const prev = byUser.get(l.user_id);
    if (!prev) {
      byUser.set(l.user_id, {
        user_id: l.user_id,
        display_name: l.display_name ?? l.email,
        email: l.email,
        count: 1,
        first_at: l.created_at,
        last_at: l.created_at,
        methods: [l.method],
      });
    } else {
      prev.count += 1;
      if (l.created_at < prev.first_at) prev.first_at = l.created_at;
      if (l.created_at > prev.last_at) prev.last_at = l.created_at;
      if (!prev.methods.includes(l.method)) prev.methods.push(l.method);
    }
  }
  return [...byUser.values()].sort((a, b) => b.last_at.localeCompare(a.last_at));
}

const LOGIN_METHOD_LABEL: Record<string, string> = {
  oidc: 'Microsoft SSO',
  otp: 'Email code',
  dev: 'Dev login',
  emergency: 'Emergency',
};

function relativeTime(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins === 0) return 'just now';
  if (mins === 1) return '1 minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
}

/**
 * Who is on the site right now, for the "is it safe to deploy?"
 * question. Connected means a live websocket this second; recently
 * active means a session that touched the API inside the window.
 * Polls rather than subscribing: the session-derived half has no push
 * event, so a 15s refetch covers both halves with no extra plumbing.
 */
export function ActiveUsers() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'activity'],
    queryFn: () => api<ActivityResponse>('/api/admin/activity'),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const logins = useQuery({
    queryKey: ['admin', 'activity', 'logins'],
    queryFn: () => api<LoginsResponse>('/api/admin/activity/logins?hours=24'),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  const users = data?.users ?? [];
  const connectedCount = users.filter((u) => u.connected).length;
  const loginRows = rollupLogins(logins.data?.logins ?? []);
  const loginTotal = logins.data?.logins.length ?? 0;

  return (
    <main className="mx-auto max-w-5xl space-y-5 px-4 py-8">
      <PageHeader
        eyebrowIcon={Radio}
        eyebrow="System"
        title="Active users"
        description={`Who is on the site right now. Connected users hold a live real-time link; recently active covers anyone whose session touched the server in the last ${
          data?.window_minutes ?? 15
        } minutes. Refreshes every 15 seconds.`}
      />

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-5">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-5 w-1/2" />
            <Skeleton className="h-5 w-3/5" />
          </div>
        </Card>
      ) : users.length === 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" />}
          title="Nobody is on the site"
          description="No live connections and no sessions active in the window. A deploy or restart will not interrupt anyone."
        />
      ) : (
        <Card>
          <div className="border-b border-divider/10 px-5 py-2.5 text-xs font-medium text-fg-muted">
            {connectedCount} connected · {users.length - connectedCount} recently active
          </div>
          <ul className="divide-y divide-divider/10">
            {users.map((u) => (
              <li key={u.user_id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="min-w-[14rem] flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-fg">{u.display_name}</span>
                    {u.portal === 'training' ? (
                      <Pill tone="info" size="sm">
                        Training portal
                      </Pill>
                    ) : null}
                  </div>
                  <div className="text-xs text-fg-muted">{u.email}</div>
                </div>
                <div className="flex min-w-[10rem] flex-none flex-wrap gap-1">
                  {u.roles.map((r) => (
                    <Pill key={r} tone="neutral" size="sm">
                      {r}
                    </Pill>
                  ))}
                </div>
                <div className="w-44 flex-none text-xs text-fg-muted">
                  {u.login_method ? (
                    <>
                      <div>{LOGIN_METHOD_LABEL[u.login_method] ?? u.login_method}</div>
                      {u.login_at ? <div>since {formatDateTime(u.login_at)}</div> : null}
                    </>
                  ) : (
                    <div>Session details unavailable</div>
                  )}
                </div>
                <div className="w-40 flex-none text-xs text-fg-muted">
                  Active {relativeTime(u.last_activity_at)}
                </div>
                <div className="w-36 flex-none text-right">
                  {u.connected ? (
                    <Pill tone="success" size="sm">
                      Connected{u.socket_count > 1 ? ` (x${u.socket_count})` : ''}
                    </Pill>
                  ) : (
                    <Pill tone="neutral" size="sm">
                      Recently active
                    </Pill>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-fg">
          <LogIn className="h-4 w-4 text-fg-muted" />
          Logins in the last 24 hours
        </h2>
        {logins.isLoading ? (
          <Card>
            <div className="space-y-3 p-5">
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-5 w-1/2" />
            </div>
          </Card>
        ) : loginRows.length === 0 ? (
          <EmptyState
            icon={<LogIn className="h-6 w-6" />}
            title="No logins in the last 24 hours"
            description="Nobody has signed in during this window. Login history is kept for 12 months."
          />
        ) : (
          <Card>
            <div className="border-b border-divider/10 px-5 py-2.5 text-xs font-medium text-fg-muted">
              {loginTotal} login{loginTotal === 1 ? '' : 's'} from {loginRows.length}{' '}
              {loginRows.length === 1 ? 'person' : 'people'}
            </div>
            <ul className="divide-y divide-divider/10">
              {loginRows.map((u) => (
                <li key={u.user_id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <div className="min-w-[14rem] flex-1">
                    <div className="font-medium text-fg">{u.display_name}</div>
                    <div className="text-xs text-fg-muted">{u.email}</div>
                  </div>
                  <div className="flex min-w-[8rem] flex-none flex-wrap gap-1">
                    {u.methods.map((m) => (
                      <Pill key={m} tone="neutral" size="sm">
                        {LOGIN_METHOD_LABEL[m] ?? m}
                      </Pill>
                    ))}
                  </div>
                  <div className="w-28 flex-none text-xs text-fg-muted">
                    {u.count} login{u.count === 1 ? '' : 's'}
                  </div>
                  <div className="w-56 flex-none text-right text-xs text-fg-muted">
                    {u.count > 1 ? (
                      <>
                        <div>last {formatDateTime(u.last_at)}</div>
                        <div>first {formatDateTime(u.first_at)}</div>
                      </>
                    ) : (
                      <div>{formatDateTime(u.last_at)}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </main>
  );
}
